import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { isToday, UserRoles } from 'common/enums';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import CardReservations from 'screens/organisme/classDetails/components/card';
import CardReservationsStudent from 'screens/student/calendar/components/card';
import TeacherCoursesRoom from '../CoursesRooms/TeacherCoursesRoom';
import { Session } from '../types';
import './SessionsListView.css';

interface SessionsListViewProps {
  sessions: any[];
  teacher: any;
}

const SessionsListView: React.FC<SessionsListViewProps> = ({
  sessions,
  teacher,
}) => {
  const { user } = useSelector((state) => state.auth);
  const isTeacher = user?.roles?.includes(UserRoles?.TEACHER);
  const isOrganism = user?.roles?.includes(UserRoles?.ORGANISME);
  const isStudent = user?.roles?.includes(UserRoles?.STUDENT);
  const [selectedDate, setSelectedDate] = useState(sessions[0]?.day || new Date().toISOString().split("T")[0]);
  const [courses, setCourses] = useState<any>([]);
  const [seeAll, setSeeAll] = useState<boolean>(true);

  const datePillsRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1050);
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 1050);
  };
  window.addEventListener('resize', handleResize);

  // Group sessions by date
  const sessionsByDate = sessions.reduce((acc, session) => {
    if (!acc[session.day]) {
      acc[session.day] = [];
    }
    acc[session.day].push(session);
    return acc;
  }, {} as Record<string, Session[]>);

  const dates = Object.keys(sessionsByDate).sort();
  const currentIndex = dates.indexOf(selectedDate);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDateShort = (dateStr: string) => {
    const date = new Date(dateStr);
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Improved scroll function
  const scrollToDate = (index: number) => {
    if (datePillsRef.current) {
      const buttons = datePillsRef.current.querySelectorAll('.date-pill, .date-pill-active');
      const targetButton = buttons[index] as HTMLElement;

      if (targetButton) {
        const container = datePillsRef.current;
        const buttonLeft = targetButton.offsetLeft;
        const buttonWidth = targetButton.offsetWidth;
        const containerWidth = container.offsetWidth;
        const currentScroll = container.scrollLeft;

        // Calculate the position to center the button
        let scrollPosition = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);

        // Ensure scroll position is never negative
        scrollPosition = Math.max(0, scrollPosition);

        // Also ensure we don't scroll past the maximum
        const maxScroll = container.scrollWidth - containerWidth;
        scrollPosition = Math.min(scrollPosition, maxScroll);

        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handlePrevDate = () => {
    setSeeAll(false);
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setSelectedDate(dates[newIndex]);

      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        setTimeout(() => scrollToDate(newIndex), 100);
      });
    }
  };

  const handleNextDate = () => {
    setSeeAll(false);
    if (currentIndex < dates.length - 1) {
      const newIndex = currentIndex + 1;
      setSelectedDate(dates[newIndex]);

      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        setTimeout(() => scrollToDate(newIndex), 100);
      });
    }
  };

  useEffect(() => {
    if (sessions) {
      const groupedByDate = sessions.reduce((acc: any, course: any) => {
        const date = new Date(course.startDate).toISOString().split("T")[0];
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(course);
        return acc;
      }, {});

      setCourses(groupedByDate);
    }
  }, [sessions]);

  return (
    <div className="sessions-list-container">
      <div className="date-navigation">
        <Button
          type="primary"
          size="middle"
          className="classBtn classBtn-teal show-all-btn"
          onClick={() => { setSeeAll(true); setSelectedDate(null) }}
        >
          Afficher tout
        </Button>
        <div className="date-navigation-row">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={handlePrevDate}
            disabled={currentIndex === 0}
            size="large"
            className="nav-arrow"
          />
          <div className="date-pills" ref={datePillsRef}>
            {dates.map((date) => (
              <Button
                key={date}
                type={date === selectedDate ? 'primary' : 'default'}
                onClick={() => { setSeeAll(false); setSelectedDate(date) }}
                className={date === selectedDate ? 'date-pill-active' : 'date-pill'}
                size="large"
              >
                {formatDateShort(date)}
              </Button>
            ))}
          </div>
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={handleNextDate}
            disabled={currentIndex === dates.length - 1}
            size="large"
            className="nav-arrow"
          />
        </div>
      </div>

      {seeAll ? <div className="sessions-list">
        {courses && Object.keys(courses)?.map((date) => {
          return (
            <>
              <div key={date} style={{ marginTop: "10px" }}>
                <div className="date-box">
                  {new Intl.DateTimeFormat("fr-FR", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }).format(new Date(date))}
                  {isToday(date) && <div className="today-box">Aujourd'hui</div>}
                  <div className="horizontal-line"></div>
                </div>
                {!isMobile && courses[date].map((course: any) => (
                  <div key={course.id}>
                    {isOrganism ? <CardReservations course={course} isHistorique={false} teacher={teacher} displayClasseName={false} /> : isTeacher ? <TeacherCoursesRoom course={course} /> : isStudent && <CardReservationsStudent course={course} isHistorique={false} teacher={teacher} />}
                  </div>
                ))}

                {isMobile && <div className="card-display">
                  {courses[date].map((course: any) => (
                    <div key={course.id}>
                      {isOrganism ? <CardReservations course={course} isHistorique={false} teacher={teacher} displayClasseName={false} /> : isTeacher ? <TeacherCoursesRoom course={course} /> : isStudent && <CardReservationsStudent course={course} isHistorique={false} teacher={teacher} />}
                    </div>
                  ))}
                </div>}
              </div>
            </>
          );
        })}
      </div> : <div className="sessions-list">
        <div className="sessions-day-title">
          {formatDate(selectedDate)}
          <div className="horizontal-line"></div>
        </div>
        {sessionsByDate[selectedDate]?.map((session: any) => (
          isOrganism ? <CardReservations course={session} isHistorique={false} teacher={teacher} displayClasseName={false} /> : <TeacherCoursesRoom course={session} />
        ))}
      </div>}
    </div>
  );
};

export default SessionsListView;